import React from 'react'
import { Notify } from '../../../text/notify/Notify'
import { BpButton } from '../../../buttons/bp-button/BpButton'
import { hideAllModal, hideModal, showAlert } from '../../../../../app/store/reducers/AppSlice'
import { useAppDispatch } from '../../../../../app/store/hooks/Redux'
import { IconArrowRightCircle } from '../../../icons/IconArrowRightCircle'
import {
  RequestChangeTariffResponse,
  TariffPrecheck,
  usePostUserRequestChangeTariffMutation,
} from '../../../../../app/api'
import { getCurrencySymbol } from '../../../../lib/helpers/get-currency-symbol'
import { cutPeriod } from '../../../../lib/helpers/cut-period'
import { useTranslation } from 'react-i18next'

interface Props {
  id: number
  period: 'annually' | 'monthly'
  tariffName: string
  data: TariffPrecheck
}

export const ConfirmModal = ({ id, tariffName, period, data }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [sendRequestChangeTariff, { isLoading }] = usePostUserRequestChangeTariffMutation()

  const handleChangeTariff = async () => {
    sendRequestChangeTariff({
      requestChangeTariffForm: {
        tariff_id: id,
        payment_periodicity: period,
        stripe_precheck: false,
      },
    })
      .unwrap()
      .then((response) => {
        if (!response.success) return

        const url = (response as RequestChangeTariffResponse).data?.session_url

        if (url) {
          window.location.href = url
        } else {
          dispatch(
            showAlert({
              message: t('notifications.paymentSuccess'),
              type: 'success',
              timeout: 5000,
            }),
          )
          dispatch(hideAllModal())
        }
      })
      .catch(() => undefined)
  }

  return (
    <div>
      <h3
        className={
          'flex min-h-[55px] w-full items-center border-b-[3px] border-b-[#00326D]/[.02] px-[24px] text-[20px] font-medium'
        }
      >
        {t('pages.tariff.reviewBeforeYouPay')}
      </h3>
      <div className={'py-[18px] px-[24px]'}>
        <Notify>{t('pages.tariff.unusedTime')}</Notify>
        <div className={'flex flex-col gap-[16px]'}>
          <div className={'flex items-start justify-between pt-[20px]'}>
            <div className={'flex flex-col text-[20px] text-focus-500'}>
              {tariffName}{' '}
              <span className={'text-[15px]'}>
                {t(`pages.tariff.billedStartingToday.${period}`)}
              </span>
            </div>
            <div className={'text-[20px] text-focus-500'}>
              {getCurrencySymbol(data.currency)}
              {t(`pages.tariff.amount.${period}`, { count: data.base_amount })}
            </div>
          </div>
          <div className={'flex items-start justify-between'}>
            <div className={'text-[20px] text-focus-500'}>
              {t('pages.tariff.taxCount', { count: data.tax_rate_percent })}
            </div>
            <div className={'text-[20px] text-focus-500'}>
              {getCurrencySymbol(data.currency)}
              {data.tax_amount}
            </div>
          </div>
          <div className={'flex items-start justify-between'}>
            <div className={'text-[20px] font-medium text-focus-500'}>
              {t('pages.tariff.totalDueToday')}
            </div>
            <div className={'text-[20px] font-medium text-focus-500'}>
              {getCurrencySymbol(data.currency)}
              {data.total_amount}
            </div>
          </div>
        </div>
      </div>
      <div
        className={
          'border-b-[3px] border-t-[3px] border-b-[#00326D]/[.02] border-t-[#00326D]/[.02] py-[24px] px-[24px]'
        }
      >
        <div className={'flex justify-between'}>
          <div className={'text-[20px] text-focus-500'}>{t('pages.tariff.paymentMethod')}</div>
          <div className={'text-[20px] text-focus-500'}>{data.payment_method.formatted}</div>
        </div>
      </div>
      <div className={'flex items-center py-[18px] px-[24px]'}>
        <BpButton
          className={'uppercase'}
          iconLeft={<IconArrowRightCircle />}
          loading={isLoading}
          onClick={handleChangeTariff}
        >
          {t('pages.tariff.buttons.payNow')}
        </BpButton>
        <BpButton theme='transparent' type='button' onClick={() => dispatch(hideModal())}>
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </div>
  )
}
